import { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { round } from 'lodash';
import { translateBackend } from '../../../assets/i18n/config';
import { formatMoneyValue, generateClientNameString } from '../../../util';
import { useThemeTokens } from '../../../providers/themeTokenProvider';
import { Box, Skeleton, Typography } from '../../1-primative';
import {
  Card, Table, TableCell, TableHeadCell, TableRow,
  TableTotalCell,
} from '../../2-component';
import { SecondaryTabs } from '../../3-pattern';
import { Ledger } from '../../4-module/widgets/ledger/ledger';
import { Transfers } from '../../4-module/widgets/transfers/transfers';
import { TradeOrders } from '../../4-module/widgets/tradeOrders/tradeOrders';
import { CustodianActivities } from '../../4-module/widgets/custodianActivities/custodianActivities';
import { PageObjectType } from '..';
import { useLocalization } from '../../../util/useLocalization';
import { AccountReconciliationSnapshot } from '../../../interfaces';

const FETCH_ACCOUNT_RECONCILIATION_SNAPSHOT = gql`
  query fetchAccountReconciliationSnapshot($accountReconciliationSnapshotId: ObjectID!) {
    fetchAccountReconciliationSnapshot(accountReconciliationSnapshotId: $accountReconciliationSnapshotId) {
      accountReconciliationSnapshot {
        id
        date
        account {
          id custodianAccountNumber type
          user { id firstName lastName entityName organization { id } }
        }
        holdings {
          financialProduct {
            id
            ticker
            translatedName {
              en
              fr
            }
          }
          oneVestQuantity
          custodianQuantity
          oneVestEodPriceCents
          custodianEodPriceCents
          oneVestBookValueCents
          custodianBookValueCents
          oneVestValueCents
          custodianValueCents
          isCash
          currency
        }
        custodianUpdatedAt
        oneVestUpdatedAt
        flags {
          type
          description
          details
          financialProduct {
            id ticker
          }
          state
        }
      }
    }
  }
`;
export const AccountSnapshot = () => {
  const { id } = useParams();
  const { t } = useTranslation(['reconciliation', 'accountTypes']);
  const { sys } = useThemeTokens();
  const [activeTab, setActiveTab] = useState('ledger');
  const { localizedDate } = useLocalization();

  const { data, loading, refetch } = useQuery(FETCH_ACCOUNT_RECONCILIATION_SNAPSHOT, {
    variables: {
      accountReconciliationSnapshotId: id,
    },
  });

  const snapshot: AccountReconciliationSnapshot = data?.fetchAccountReconciliationSnapshot?.accountReconciliationSnapshot;
  const organizationId = snapshot?.account?.user?.organization?.id;

  return (
    <>
      <Box display='flex' justifyContent='space-between'>
        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant='text' width='300px' height='36px' />
            <Skeleton variant='text' width='200px' height='28px' />
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant='headingMedium'>
              {generateClientNameString(snapshot?.account?.user, false, false)}{' '}
              - {t(`accountTypes:${snapshot?.account?.type}`)}
            </Typography>
            <Typography variant='headingSmall'>
              {snapshot?.account?.custodianAccountNumber}
            </Typography>
          </Box>
        )}
        <Typography variant='headingSmall'>
          {localizedDate(snapshot?.date)}
        </Typography>
      </Box>
      <Card sx={{ overflowX: 'auto' }}>
        <Table>
          <TableRow>
            <TableHeadCell isFirst>{t('accountSnapshot.security')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.systemQuantity')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.externalQuantity')}</TableHeadCell>
            <TableHeadCell right sx={{ borderRight: `1px solid ${sys.color.outline}` }}>{t('accountSnapshot.diffQuantity')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.systemBookValue')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.externalBookValue')}</TableHeadCell>
            <TableHeadCell right sx={{ borderRight: `1px solid ${sys.color.outline}` }}>{t('accountSnapshot.diffBookValue')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.systemPrice')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.externalPrice')}</TableHeadCell>
            <TableHeadCell right sx={{ borderRight: `1px solid ${sys.color.outline}` }}>{t('accountSnapshot.diffPrice')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.systemValue')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.externalValue')}</TableHeadCell>
            <TableHeadCell right>{t('accountSnapshot.diffValue')}</TableHeadCell>
          </TableRow>
          {loading && [...Array(15)].map((x: any, i: number) => (
            <TableRow key={i}>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
              <TableCell><Skeleton width='100%' /></TableCell>
            </TableRow>
          ))}
          {snapshot?.holdings.map((holding) => (
            <TableRow hover>
              <TableCell isFirst sx={{
                whiteSpace: 'nowrap', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                <b>{holding.isCash ? holding.currency : holding.financialProduct.ticker}</b> - {translateBackend(holding.financialProduct.translatedName)}
              </TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestQuantity !== holding.custodianQuantity ? sys.color.negativeVariant : undefined,
              }}>{holding.oneVestQuantity}</TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestQuantity !== holding.custodianQuantity ? sys.color.negativeVariant : undefined,
              }}>{holding.custodianQuantity}</TableCell>
              <TableCell number sx={{
                borderRight: `1px solid ${sys.color.outline}`,
                backgroundColor: holding.oneVestQuantity !== holding.custodianQuantity ? sys.color.negativeVariant : undefined,
              }}>
                {round((holding.oneVestQuantity ?? 0) - (holding.custodianQuantity ?? 0), 2)}
              </TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestBookValueCents !== holding.custodianBookValueCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.oneVestBookValueCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestBookValueCents !== holding.custodianBookValueCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.custodianBookValueCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                borderRight: `1px solid ${sys.color.outline}`,
                backgroundColor: holding.oneVestBookValueCents !== holding.custodianBookValueCents ? sys.color.negativeVariant : undefined,
              }}>
                {formatMoneyValue((holding.oneVestBookValueCents ?? 0) - (holding.custodianBookValueCents ?? 0), '$', 4)}
              </TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestEodPriceCents !== holding.custodianEodPriceCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.oneVestEodPriceCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestEodPriceCents !== holding.custodianEodPriceCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.custodianEodPriceCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                borderRight: `1px solid ${sys.color.outline}`,
                backgroundColor: holding.oneVestEodPriceCents !== holding.custodianEodPriceCents ? sys.color.negativeVariant : undefined,
              }}>
                {formatMoneyValue((holding.oneVestEodPriceCents ?? 0) - (holding.custodianEodPriceCents ?? 0), '$', 4)}
              </TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestValueCents !== holding.custodianValueCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.oneVestValueCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestValueCents !== holding.custodianValueCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue(holding.custodianValueCents, '$', 4)}</TableCell>
              <TableCell number sx={{
                backgroundColor: holding.oneVestValueCents !== holding.custodianValueCents ? sys.color.negativeVariant : undefined,
              }}>{formatMoneyValue((holding.oneVestValueCents ?? 0) - (holding.custodianValueCents ?? 0), '$', 4)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableTotalCell bold isFirst>{t('accountSnapshot.total')}</TableTotalCell>
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell sx={{ borderRight: `1px solid ${sys.color.outline}` }} />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell sx={{ borderRight: `1px solid ${sys.color.outline}` }} />
            <TableTotalCell />
            <TableTotalCell />
            <TableTotalCell sx={{ borderRight: `1px solid ${sys.color.outline}` }} />
            <TableTotalCell number bold>
              {formatMoneyValue(snapshot?.holdings.reduce((sum: number, holding) => sum + (holding.oneVestValueCents ?? 0), 0))}
            </TableTotalCell>
            <TableTotalCell number bold>
              {formatMoneyValue(snapshot?.holdings.reduce((sum: number, holding) => sum + (holding.custodianValueCents ?? 0), 0))}
            </TableTotalCell>
            <TableTotalCell number bold>
              {formatMoneyValue(snapshot?.holdings.reduce(
                (sum: number, holding) => sum + ((holding.oneVestValueCents ?? 0) - (holding.custodianValueCents ?? 0)),
                0,
              ))}
            </TableTotalCell>
          </TableRow>
        </Table>
      </Card>
      <SecondaryTabs
        tabs={[
          { label: t('accountSnapshot.ledger'), icon: '', key: 'ledger' },
          { label: t('accountSnapshot.transfers'), icon: '', key: 'transfers' },
          { label: t('accountSnapshot.tradeOrders'), icon: '', key: 'tradeOrders' },
          { label: t('accountSnapshot.custodianActivities'), icon: '', key: 'activities' },
        ]}
        activeTab={activeTab} setActiveTab={(tab) => {
          setActiveTab(tab.toString());
        }}
      />
      {activeTab === 'ledger' && <Ledger objectId={snapshot?.account.id} objectType={PageObjectType.ACCOUNT} options={{
        hideTitle: true,
        refreshSync: true,
        canAdd: true,
        canEdit: true,
        canDelete: true,
        enableDownload: true,
        enableFilter: true,
        showOnlyRelevantTransactionTypes: true,
        organizationId,
        table: [
          { label: { en: t('components:activityTable.type'), fr: t('components:activityTable.type') }, type: 'type' as const },
          { label: { en: t('components:trades.security'), fr: t('components:trades.security') }, type: 'security' as const },
          { label: { en: t('components:tradeTable.currency'), fr: t('components:tradeTable.currency') }, type: 'currency' as const },
          { label: { en: t('components:transaction.table.priceCents'), fr: t('components:transaction.table.priceCents') }, type: 'costPrice' as const },
          { label: { en: t('components:trades.quantity'), fr: t('components:trades.quantity') }, type: 'quantity' as const },
          { label: { en: t('components:tradeTable.totalValue'), fr: t('components:tradeTable.totalValue') }, type: 'value' as const },
          { label: { en: t('components:transaction.table.date'), fr: t('components:transaction.table.date') }, type: 'date' as const },
        ],
      }} afterUpdate={refetch}/>}
      {activeTab === 'transfers' && <Transfers objectId={snapshot?.account.id} objectType={PageObjectType.ACCOUNT} options={{
        canEdit: true,
        canAdd: true,
        canTransition: true,
        enableDownload: true,
      }} />}
      {activeTab === 'tradeOrders' && <TradeOrders objectId={snapshot?.account.id} objectType={PageObjectType.ACCOUNT} options={{
        canAddNewTradeOrder: true,
        canEdit: true,
        canTransition: true,
        enableFilter: true,
        enableDownload: true,
        organizationId,
        filterForInitiated: true,
        filterForProcessing: true,
        filterForReconciled: true,
        filterForFailed: true,
        filterForCanceled: true,
      }} />}
      {activeTab === 'activities' && <CustodianActivities accountId={snapshot?.account.id} organizationId={organizationId} options={{
        enableDownload: true,
        enableFilter: true,
        enableReconcileAction: true,
        enableForceReconcileAction: true,
      }}/>}
    </>
  );
};
